'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { useShowActiveBrandSection } from '@/frontastic/hooks/useShowActiveBrandSection';
import { ParamsObject } from '@/frontastic/tastics/types';
import useSiteTranslation from '@/providers/i18n/hooks/useSiteTranslation';
import CatalogIDSection from './form-sections/catalogID';
import CustomerIdentifierSection from './form-sections/customerIdentifier';
import EmailAddressSection from './form-sections/emailAddress';
import PhoneNumberSection from './form-sections/phoneNumber';
import CustomerSearchRadioGroup from './form-sections/radioGroup';
import {
  FORM_FIELD_SPECS,
  SEARCH_CUSTOMER_PAGE_PATH,
  CREATE_CUSTOMER_PAGE_PATH,
  PAGE_TEXTS_KEYS,
} from '../../../../../../constants/constant';
import { Card, CardHeader, CardTitle } from '../../../ui/card';
import { TabsContent } from '../../../ui/tabs';
import { CUSTOMER_SEARCH_TYPES } from '../../constants';
import { CustomerAndOrderSearchPropsTypes } from '../../types';
import ZipLNameFNameSection from '../common/form-sections/zipCode';

const Index = ({
  setSelectedSearchType,
  selectedSearchType,
  searchParams,
  setSearchParams,
  handleSearch,
}: CustomerAndOrderSearchPropsTypes) => {
  const Texts = useSiteTranslation(PAGE_TEXTS_KEYS?.customerAndOrderSearch);
  const router = useRouter();
  const showActiveBrandSection = useShowActiveBrandSection();

  const {
    setValue,
    register,
    handleSubmit,
    formState: { errors },
    reset: resetCatalogIdForm,
  } = useForm({
    mode: 'onBlur',
  });

  const {
    setValue: setOtherIdentifierValue,
    register: registerOtherCustomerIdentifier,
    handleSubmit: handleSubmitOtherCustomerIdentifier,
    reset: resetOtherCustomerIdentifierForm,
    formState: { errors: otherCustomerIdentifierErrors },
  } = useForm({
    mode: 'onBlur',
  });

  const onSubmit = (params: ParamsObject) => {
    handleSearch(params);
  };

  const [formWrapperKey, setFormWrapperKey] = useState({
    [FORM_FIELD_SPECS?.catalogId?.id]: `key_${selectedSearchType}_${FORM_FIELD_SPECS?.catalogId?.id}`,
    [FORM_FIELD_SPECS?.otherIdentifierId?.id]: `key_${selectedSearchType}_${FORM_FIELD_SPECS?.otherIdentifierId?.id}`,
  });

  return (
    <TabsContent value={SEARCH_CUSTOMER_PAGE_PATH}>
      <Card>
        <CardHeader>
          <CardTitle className="mb-4 flex justify-between">
            <span className="text-lg font-bold">{Texts?.customerSearchHeading}</span>
            {showActiveBrandSection && (
              <button
                type="button"
                className="text-14 font-normal text-[#4784E2] underline"
                onClick={() => router.push(`/${CREATE_CUSTOMER_PAGE_PATH}`)}
              >
                {Texts?.createNewCustomerLink}
              </button>
            )}
          </CardTitle>
          <CustomerSearchRadioGroup
            selectedSearchType={selectedSearchType}
            setSearchParams={setSearchParams}
            CUSTOMER_SEARCH_TYPES={CUSTOMER_SEARCH_TYPES}
            setSelectedSearchType={setSelectedSearchType}
          />
        </CardHeader>
        <section className="relative flex pb-10">
          <section className="lg:w-1/2 lg:pr-16">
            <form
              onSubmit={handleSubmit(onSubmit)}
              id="catalogIdForm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit(onSubmit)();
                }
              }}
            >
              {selectedSearchType === CUSTOMER_SEARCH_TYPES?.customerIdentifier && (
                <CatalogIDSection
                  setSearchParams={setSearchParams}
                  formWrapperKey={formWrapperKey}
                  setFormWrapperKey={setFormWrapperKey}
                  resetFormOnInteraction={resetOtherCustomerIdentifierForm}
                  FORM_FIELD_SPECS={FORM_FIELD_SPECS}
                  selectedSearchType={selectedSearchType}
                  searchParams={searchParams}
                  errors={errors}
                  register={register}
                  setValue={setValue}
                />
              )}
              {selectedSearchType === CUSTOMER_SEARCH_TYPES?.phoneNumber && (
                <PhoneNumberSection
                  FORM_FIELD_SPECS={FORM_FIELD_SPECS}
                  selectedSearchType={selectedSearchType}
                  searchParams={searchParams}
                  errors={errors}
                  register={register}
                />
              )}
              {selectedSearchType === CUSTOMER_SEARCH_TYPES?.emailAddress && (
                <EmailAddressSection
                  FORM_FIELD_SPECS={FORM_FIELD_SPECS}
                  selectedSearchType={selectedSearchType}
                  searchParams={searchParams}
                  errors={errors}
                  register={register}
                  setValue={setValue}
                />
              )}
              {selectedSearchType === CUSTOMER_SEARCH_TYPES?.zipFnameLname && (
                <ZipLNameFNameSection
                  FORM_FIELD_SPECS={FORM_FIELD_SPECS}
                  selectedSearchType={selectedSearchType}
                  searchParams={searchParams}
                  errors={errors}
                  register={register}
                />
              )}
            </form>
          </section>
          {selectedSearchType === CUSTOMER_SEARCH_TYPES?.customerIdentifier && (
            <CustomerIdentifierSection
              setSearchParams={setSearchParams}
              formWrapperKey={formWrapperKey}
              setFormWrapperKey={setFormWrapperKey}
              resetFormOnInteraction={resetCatalogIdForm}
              selectedSearchType={selectedSearchType}
              FORM_FIELD_SPECS={FORM_FIELD_SPECS}
              handleSubmitOtherCustomerIdentifier={handleSubmitOtherCustomerIdentifier}
              onSubmit={onSubmit}
              registerOtherCustomerIdentifier={registerOtherCustomerIdentifier}
              otherCustomerIdentifierErrors={otherCustomerIdentifierErrors}
              searchParams={searchParams}
              setOtherIdentifierValue={setOtherIdentifierValue}
            />
          )}
        </section>
      </Card>
    </TabsContent>
  );
};

export default Index;
